import {
  createLocation,
  getLocationRecord,
  listCompanyRecordsAuto,
  upsertLocation,
} from '../api/companyRecords';
import {
  DEFAULT_LOCATION_NAME,
  normalizeLocationAddress,
  normalizeLocationIds,
} from './locationTypes';

const mapLocation = (location) => {
  const data = location || {};
  return {
    ...data,
    id: data.id,
    address: normalizeLocationAddress(data.address),
    locationIds: normalizeLocationIds(data.locationIds),
  };
};

const sortLocations = (locations) => (
  [...locations].sort((a, b) => {
    const sortA = Number.isFinite(a.sortOrder) ? a.sortOrder : 999;
    const sortB = Number.isFinite(b.sortOrder) ? b.sortOrder : 999;
    if (sortA !== sortB) return sortA - sortB;
    return String(a.name || '').localeCompare(String(b.name || ''));
  })
);

export const fetchCompanyLocations = async (companyId, { activeOnly = false } = {}) => {
  if (!companyId) return [];

  const records = await listCompanyRecordsAuto({ companyId, kind: 'locations' });
  let locations = records.map(mapLocation);

  if (activeOnly) {
    locations = locations.filter((location) => location.active !== false);
  }

  return sortLocations(locations);
};

export const fetchActiveLocations = async (companyId) => (
  fetchCompanyLocations(companyId, { activeOnly: true })
);

export const fetchCompanyLocationById = async (companyId, locationId) => {
  if (!companyId || !locationId) return null;

  try {
    const location = await getLocationRecord({ companyId, id: locationId });
    return location ? mapLocation(location) : null;
  } catch (error) {
    if (error.status === 404) return null;
    throw error;
  }
};

export const createCompanyLocation = async (companyId, locationData = {}) => {
  if (!companyId) throw new Error('companyId is required');

  const existingLocations = await fetchCompanyLocations(companyId);
  const isFirstLocation = existingLocations.length === 0;
  const now = new Date().toISOString();

  const payload = {
    name: String(locationData.name || DEFAULT_LOCATION_NAME).trim() || DEFAULT_LOCATION_NAME,
    address: normalizeLocationAddress(locationData.address),
    phone: String(locationData.phone || '').trim(),
    timezone: String(locationData.timezone || '').trim(),
    businessHours: locationData.businessHours || null,
    openOnHolidays: locationData.businessHours
      ? (locationData.openOnHolidays === true)
      : null,
    active: locationData.active !== false,
    isDefault: locationData.isDefault === true || isFirstLocation,
    sortOrder: Number.isFinite(locationData.sortOrder)
      ? locationData.sortOrder
      : existingLocations.length,
    createdAt: now,
    updatedAt: now,
  };

  const created = await createLocation({ companyId, location: payload });
  return mapLocation(created);
};

export const updateCompanyLocation = async (companyId, locationId, locationData = {}) => {
  if (!companyId || !locationId) throw new Error('companyId and locationId are required');

  const payload = {
    updatedAt: new Date().toISOString(),
  };

  if (locationData.name != null) {
    payload.name = String(locationData.name).trim() || DEFAULT_LOCATION_NAME;
  }
  if (locationData.address != null) {
    payload.address = normalizeLocationAddress(locationData.address);
  }
  if (locationData.phone != null) {
    payload.phone = String(locationData.phone).trim();
  }
  if (locationData.timezone != null) {
    payload.timezone = String(locationData.timezone).trim();
  }
  if (locationData.useCompanyHours === true || locationData.businessHours === null) {
    payload.businessHours = null;
    payload.openOnHolidays = null;
  } else if (locationData.businessHours != null) {
    payload.businessHours = locationData.businessHours;
    if (locationData.openOnHolidays != null) {
      payload.openOnHolidays = locationData.openOnHolidays === true;
    }
  }
  if (locationData.active != null) {
    payload.active = locationData.active !== false;
  }
  if (locationData.sortOrder != null) {
    payload.sortOrder = Number(locationData.sortOrder) || 0;
  }
  if (locationData.isDefault === true) {
    payload.isDefault = true;
  }

  const updated = await upsertLocation({ companyId, id: locationId, location: payload });
  return mapLocation(updated);
};

export const getDefaultLocation = (locations = []) => {
  if (!Array.isArray(locations) || locations.length === 0) return null;
  return locations.find((location) => location.isDefault)
    || locations.find((location) => location.active !== false)
    || locations[0];
};
